import { NextRouter } from 'next/router';
import { usePrivateStore } from '@/store/usePrivateStore';
import { CONSTANTS } from '@/utils/constants';
import {
  CongViecDuAnSession,
  getCongViecDuAnSession,
  getIdCongViecGanNhatFromDuAnCongViecTree,
} from './du-an-utils';
import { TableRecord } from '../CongViecDuAn/Right/Table/types';

const NOTIFY = CONSTANTS.ACTION_PLAN.NOTIFY;

export type ActionPlanAssignNotifyKind = 'new_du_an' | 'edit_du_an' | 'cong_viec_change';
export type ActionPlanAssignNotificationData = {
  idSelect?: string;
  idDuAn?: string;
  idCongViecIds?: string[];
  notifyKind?: ActionPlanAssignNotifyKind;
  /** Danh sách người phụ trách mới (comma-separated) — dùng khi nhận event edit. */
  nguoiPhuTrachID?: string;
  /** Người phụ trách trước khi đổi — tab cũ cần reload. */
  nguoiPhuTrachOldID?: string;
  /** Tab gửi event — tab nhận trùng id thì không hiện banner. */
  sourceTabId?: string;
};
const ACTION_PLAN_TAB_INSTANCE_KEY = 'action-plan-tab-instance-id';

/**
 * Chỉ hiện banner trên du-an / cong-viec-du-an khi:
 * - cùng phòng ban / nhân viên (idSelect),
 * - `cong_viec_change`: mọi nơi cùng idSelect,
 * - `new_du_an` / `edit_du_an`: theo idSelect hoặc phụ trách liên quan,
 * - tab nguồn gửi event không tự hiện banner.
 */
export const shouldShowActionPlanAssignBanner = (
  notification: { data?: ActionPlanAssignNotificationData },
  router: Pick<NextRouter, 'pathname' | 'query'>
): boolean => {
  if (!isActionPlanAssignTargetPage(router.pathname)) return false;

  const eventIdSelect = notification.data?.idSelect ?? '';
  const eventIdDuAn = notification.data?.idDuAn ?? '';
  const eventCongViecIds = notification.data?.idCongViecIds ?? [];
  const notifyKind = notification.data?.notifyKind;
  if (!eventIdSelect || !eventIdDuAn) return false;

  const sourceTabId = notification.data?.sourceTabId;
  if (!sourceTabId || sourceTabId === getActionPlanTabInstanceId()) return false;


  const preferredSelectId =
    usePrivateStore.getState().getRequestHeader?.()?.selectTree?.idSelected ?? '';
  if (notifyKind === 'new_du_an') {
    return eventIdSelect === preferredSelectId;
  }

  if (notifyKind === 'edit_du_an') {
    return (
      eventIdSelect === preferredSelectId ||
      phuTrachMatchesIdSelect(notification.data?.nguoiPhuTrachID ?? '', preferredSelectId) ||
      phuTrachMatchesIdSelect(notification.data?.nguoiPhuTrachOldID ?? '', preferredSelectId)
    );
  }

  if (notifyKind === 'cong_viec_change') {
    // Mọi nơi (du-an / cong-viec-du-an) cùng idSelect đều hiện banner khi có update công việc.
    return eventIdSelect === preferredSelectId;
  }

  if (router.pathname.includes('cong-viec-du-an')) {
    const params = Array.isArray(router.query.params) ? router.query.params : [];
    const urlIdDuAn = String(params[3] ?? '');
    if (urlIdDuAn && urlIdDuAn !== 'undefined' && urlIdDuAn === eventIdDuAn) {
      return true;
    }
  }

  const session = getCongViecDuAnSession();
  if (!session) return false;
  if ((session.idSelect ?? '') !== eventIdSelect) return false;
  if (!isProjectInSession(session, eventIdDuAn)) return false;

  if (!eventCongViecIds.length) return true;

  const sessionCongViecIds = getSessionCongViecIdsForProject(session, eventIdDuAn);
  if (!sessionCongViecIds.length) return true;

  return hasIdsOverlap(sessionCongViecIds, eventCongViecIds);
};

/** Mỗi tab có id riêng (sessionStorage theo tab). */
export const getActionPlanTabInstanceId = (): string => {
  if (typeof window === 'undefined') return '';
  try {
    let id = window.sessionStorage.getItem(ACTION_PLAN_TAB_INSTANCE_KEY);
    if (!id) {
      id = `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
      window.sessionStorage.setItem(ACTION_PLAN_TAB_INSTANCE_KEY, id);
    }
    return id;
  } catch {
    return '';
  }
};

/** Event do chính tab đang thao tác gửi → không hiện banner trên tab đó. */
export const isNotificationFromCurrentTab = (
  data?: ActionPlanAssignNotificationData
): boolean => {
  const sourceTabId = data?.sourceTabId;
  if (!sourceTabId) return false;
  return sourceTabId === getActionPlanTabInstanceId();
};

export const withActionPlanNotifySourceTab = <T extends Record<string, unknown>>(
  data: T
): T & { sourceTabId: string } => ({
  ...data,
  sourceTabId: getActionPlanTabInstanceId(),
});

/** Nhãn phạm vi cây đang chọn: có `_nv` là nhân viên, không thì phòng ban. */
export const getSelectScopeLabel = (idSelect: string): string =>
  idSelect.includes('_nv') ? NOTIFY.SCOPE_LABEL_NV : NOTIFY.SCOPE_LABEL_PB;

export const splitPhuTrachNotifyTargets = (nguoiPhuTrachID: string): string[] => {
  if (!nguoiPhuTrachID) return [];
  return [...new Set(nguoiPhuTrachID.split(',').map((x) => x.trim()).filter(Boolean))];
};

/** Gộp id phòng ban / nhân viên cần nhận thông báo (phụ trách cũ + mới). */
export const mergePhuTrachNotifyTargets = (...sources: (string | undefined)[]): string[] => {
  const set = new Set<string>();
  sources.forEach((src) => splitPhuTrachNotifyTargets(src || '').forEach((id) => set.add(id)));
  return [...set];
};

/** Từ bảng công việc: lấy idSelect cũ + mới của các dòng đổi phụ trách. */
export const collectPhuTrachNotifyTargetsFromChangedRecords = (records: TableRecord[]): string[] => {
  const ids = new Set<string>();
  const walk = (items: TableRecord[]) => {
    for (const r of items || []) {
      if (r?._phuTrachChanged === true) {
        mergePhuTrachNotifyTargets(r._phuTrachOld, r?.congViec?.nguoiPhuTrachID).forEach((id) =>
          ids.add(id)
        );
      }
      if (Array.isArray(r?.childs) && r.childs.length > 0) walk(r.childs as TableRecord[]);
    }
  };
  walk(records);
  return [...ids];
};

/** Chỉ id phụ trách cũ (trước khi user đổi trên bảng). */
export const collectPhuTrachOldIdsFromChangedRecords = (records: TableRecord[]): string => {
  const ids = new Set<string>();
  const walk = (items: TableRecord[]) => {
    for (const r of items || []) {
      if (r?._phuTrachChanged === true && r._phuTrachOld) {
        splitPhuTrachNotifyTargets(String(r._phuTrachOld)).forEach((id) => ids.add(id));
      }
      if (Array.isArray(r?.childs) && r.childs.length > 0) walk(r.childs as TableRecord[]);
    }
  };
  walk(records);
  return [...ids].join(',');
};

/** Người phụ trách dự án trùng với idSelect đang chọn trên cây. */
export const phuTrachMatchesIdSelect = (nguoiPhuTrachID: string, idSelect: string): boolean => {
  if (!nguoiPhuTrachID || !idSelect) return false;
  return splitPhuTrachNotifyTargets(nguoiPhuTrachID).includes(idSelect);
};

export const resolveIdDuAnFromEditDuAnResponse = (
  response: { data?: unknown } | null | undefined,
  fallbackId?: string
): string => {
  const data = response?.data as Record<string, unknown> | string | undefined;
  if (typeof data === 'string' && data && data !== 'temp') return data;
  if (data && typeof data === 'object') {
    const duAnCongViec = data.duAnCongViec as { congViec?: { id?: string } } | undefined;
    const fromCongViec = duAnCongViec?.congViec?.id;
    if (fromCongViec && String(fromCongViec) !== 'temp') return String(fromCongViec);
    const fromId = data.id;
    if (fromId && String(fromId) !== 'temp') return String(fromId);
  }
  if (fallbackId && fallbackId !== 'temp') return String(fallbackId);
  return '';
};

export const buildDuAnAssignNotifyContent = (
  notifyKind: 'new_du_an' | 'edit_du_an',
  tenDuAn: string,
  idSelectForEdit?: string
): { title: string; body: string; i18nTitleKey: string; i18nBodyKey: string } => {
  const safeName = tenDuAn?.trim() || NOTIFY.DEFAULT_DU_AN_NAME;
  if (notifyKind === 'new_du_an') {
    return {
      title: NOTIFY.DU_AN_MOI.TITLE,
      body: NOTIFY.DU_AN_MOI.BODY(safeName),
      i18nTitleKey: NOTIFY.DU_AN_MOI.I18N_TITLE,
      i18nBodyKey: NOTIFY.DU_AN_MOI.I18N_BODY,
    };
  }
  const scopeLabel = getSelectScopeLabel(idSelectForEdit || '');
  return {
    title: NOTIFY.DU_AN_CAP_NHAT.TITLE,
    body: NOTIFY.DU_AN_CAP_NHAT.BODY(safeName, scopeLabel),
    i18nTitleKey: NOTIFY.DU_AN_CAP_NHAT.I18N_TITLE,
    i18nBodyKey: NOTIFY.DU_AN_CAP_NHAT.I18N_BODY,
  };
};

/** Nội dung notify sau editcongviecs (tạo mới / đổi phụ trách / cập nhật thường). */
export const buildCongViecAssignNotifyContent = (
  options: { hasAnyNewTasks: boolean; hasPhuTrachChanges: boolean; tenDuAn: string }
): { title: string; body: string; i18nTitleKey: string; i18nBodyKey: string } => {
  const safeName = options.tenDuAn?.trim() || NOTIFY.DEFAULT_DU_AN_NAME;
  if (options.hasAnyNewTasks) {
    return {
      title: NOTIFY.CONG_VIEC_MOI.TITLE,
      body: NOTIFY.CONG_VIEC_MOI.BODY(safeName),
      i18nTitleKey: NOTIFY.CONG_VIEC_MOI.I18N_TITLE,
      i18nBodyKey: NOTIFY.CONG_VIEC_MOI.I18N_BODY,
    };
  }
  if (options.hasPhuTrachChanges) {
    return {
      title: NOTIFY.DOI_PHU_TRACH.TITLE,
      body: NOTIFY.DOI_PHU_TRACH.BODY(safeName),
      i18nTitleKey: NOTIFY.DOI_PHU_TRACH.I18N_TITLE,
      i18nBodyKey: NOTIFY.DOI_PHU_TRACH.I18N_BODY,
    };
  }
  return {
    title: NOTIFY.CONG_VIEC_CAP_NHAT.TITLE,
    body: NOTIFY.CONG_VIEC_CAP_NHAT.BODY(safeName),
    i18nTitleKey: NOTIFY.CONG_VIEC_CAP_NHAT.I18N_TITLE,
    i18nBodyKey: NOTIFY.CONG_VIEC_CAP_NHAT.I18N_BODY,
  };
};

/** Chuẩn hóa body banner (giữ tương thích payload cũ). */
export const stripDuAnBannerReloadHint = (text: string): string =>
  text
    .replace(/\s*Vui lòng tải lại danh sách nếu cần\.?/gi, ` ${NOTIFY.RELOAD_HINT}`)
    .replace(/\s*Vui lòng tải lại dữ liệu nếu cần\.?/gi, ` ${NOTIFY.RELOAD_HINT}`)
    .trim();


export const isActionPlanAssignTargetPage = (pathname: string): boolean => {
  if (!pathname) return false;
  if (pathname.includes('cong-viec-du-an')) return true;
  return pathname === '/du-an' || pathname.endsWith('/du-an') || pathname === '/action-plan-v2' || pathname.endsWith('/action-plan-v2');
};

/** Trang danh sách dự án `/du-an` (không gồm cong-viec-du-an). */
export const isDuAnIndexPage = (pathname: string): boolean => {
  if (!pathname) return false;
  if (pathname.includes('cong-viec-du-an')) return false;
  return pathname === '/du-an' || pathname.endsWith('/du-an') || pathname === '/action-plan-v2' || pathname.endsWith('/action-plan-v2');
};

export const ACTION_PLAN_ASSIGN_RECEIVED_EVENT = 'action-plan-assign-received';

/** Tab nhận SignalR (sau khi clear session) — trang du-an lắng nghe để fetchDashboard. */
export const emitActionPlanAssignReceived = (
  data?: ActionPlanAssignNotificationData
): void => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(
    new CustomEvent(ACTION_PLAN_ASSIGN_RECEIVED_EVENT, { detail: data ?? {} })
  );
};

const hasIdsOverlap = (a: string[], b: string[]): boolean => {
  if (!a.length || !b.length) return false;
  const set = new Set(b);
  return a.some((id) => set.has(id));
};

const getSessionCongViecIdsForProject = (
  session: CongViecDuAnSession,
  idDuAn: string
): string[] => getIdCongViecGanNhatFromDuAnCongViecTree(idDuAn, session.listDuAn ?? [], session);

const isProjectInSession = (session: CongViecDuAnSession, idDuAn: string): boolean => {
  const inList = (session.listDuAn ?? []).some((p) => p.congViec?.id === idDuAn);
  const sessionDuAnId = session.currentDuAn?.congViec?.id;
  return inList || sessionDuAnId === idDuAn;
};
